import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useApi } from '../../../../hooks/useApi';
import {
  vitrineGetSettings,
  vitrineSaveSettings,
  vitrineRequestCheck,
  vitrineManualCostPreview,
  vitrineAutoCostPreview,
  vitrinePollTask,
  vitrineListTasks,
  vitrineGetPricePivot,
  vitrineGetGroupedPivot,
  vitrineRequeueTask,
  vitrineCancelTask,
} from '../../../../services/ozonVitrine';
import { emptySchedule, ScheduleGrid } from './scheduleGrid';
import LocalAgentSetup from './LocalAgentSetup';
import './OzonVitrineMonitor.css';
import { fmtDateTime } from '../../../../utils/ruDates';
import { filterBySearch } from '../../../../utils/search';

const PIVOT_PAGE_SIZE = 50;

function recommendedDaytimeSchedule(intervalHours) {
  const step = Math.max(1, Number(intervalHours) || 1);
  const next = emptySchedule();
  for (let hour = 8; hour < 24; hour += step) {
    for (let day = 0; day < 7; day += 1) next[hour * 7 + day] = true;
  }
  return next;
}

function formatDuration(minutes) {
  const value = Math.max(1, Number(minutes) || 1);
  if (value < 60) return `около ${value} мин`;
  const hours = Math.floor(value / 60);
  const rest = value % 60;
  return `около ${hours} ч${rest ? ` ${rest} мин` : ''}`;
}

function fmtDT(iso) {
  if (!iso) return '—';
  try {
    return fmtDateTime(iso);
  } catch { return iso; }
}

function fmtPrice(v) {
  if (v == null) return '—';
  return Number(v).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) + ' ₽';
}

function fmtCoinvest(pct) {
  if (!pct || Number(pct) === 0) return null;
  return `−${Number(pct).toFixed(1)}%`;
}


// ── Confirm modal (cost breakdown before spending money) ──────────────────

function ConfirmModal({ title, lines, confirmLabel, onConfirm, onCancel, loading }) {
  return (
    <div className="vm-modal-overlay" onClick={onCancel}>
      <div className="vm-modal" onClick={e => e.stopPropagation()}>
        <h4 className="vm-modal__title">{title}</h4>
        <div className="vm-modal__body">
          {lines.map((l, i) => <p key={i} className="vm-modal__line">{l}</p>)}
        </div>
        <div className="vm-modal__actions">
          <button className="btn btn--ghost btn--sm" onClick={onCancel} disabled={loading}>Отмена</button>
          <button className="btn btn--primary btn--sm" onClick={onConfirm} disabled={loading}>
            {loading ? '...' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Paste article list ──────────────────────────────────────────────────

function PasteBox({ onApply, onClearAll }) {
  const [text, setText] = useState('');
  const apply = () => {
    // Split on newline/comma/semicolon only — an article code itself can
    // contain spaces (e.g. "Воблер HIGASHI Deep Bro 90F 16g 02"), so a plain
    // whitespace split would shred it into garbage fragments.
    const tokens = text.split(/[\n,;]+/).map(t => t.trim()).filter(Boolean);
    if (tokens.length) onApply(tokens);
    setText('');
  };
  return (
    <div className="vm-paste">
      <textarea
        className="vm-paste__area"
        placeholder="Вставьте артикулы через запятую или каждый с новой строки (пробелы внутри артикула — ок) — совпавшие отметятся в списке ниже"
        value={text}
        onChange={e => setText(e.target.value)}
        rows={2}
      />
      <div className="vm-paste__btns">
        <button className="btn btn--ghost btn--sm vm-paste__btn" onClick={apply} disabled={!text.trim()}>
          Отметить в списке
        </button>
        <button className="btn btn--ghost btn--sm vm-paste__btn" onClick={onClearAll}>
          Сбросить все
        </button>
      </div>
    </div>
  );
}

// ── Monitor config: assortment (step 1) + mode (step 2) ────────────────────
// Assortment/mode state is owned by the parent (OzonVitrineMonitor) and passed
// down as controlled props, because the same selection is also rendered as
// checkboxes in the price table below (PivotTable) — one selection, one place
// it's edited from, instead of a second standalone product list here.

function MonitorConfigPanel({
  accountId,
  workspaceId,
  executor, setExecutor,
  legacyParserMarket,
  scenarioType, setScenarioType,
  customSource, setCustomSource,
  customValue, setCustomValue,
  region,
  mode, setMode,
  schedule, setSchedule,
  isActive, setIsActive,
  selectedCount,
  offerIds,
  onPasteApply,
  onClearAll,
  loadingCheck, onRun,
  loading,
}) {
  const { request, showStatus } = useApi();
  const [saving, setSaving] = useState(false);

  // confirm modal state: { type: 'manual'|'auto', preview }
  const [confirm, setConfirm] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [timeEstimate, setTimeEstimate] = useState(null);

  useEffect(() => {
    let cancelled = false;
    if (executor !== 'windows_agent') {
      setTimeEstimate(null);
      return () => { cancelled = true; };
    }
    vitrineManualCostPreview({ request, accountId, workspaceId })
      .then(preview => {
        if (!cancelled && preview?.estimated_minutes) setTimeEstimate(preview);
      })
      .catch(() => {
        if (!cancelled) setTimeEstimate(null);
      });
    return () => { cancelled = true; };
  }, [request, accountId, workspaceId, executor]);

  const applyRecommendedSchedule = () => {
    if (!timeEstimate) return;
    setSchedule(recommendedDaytimeSchedule(timeEstimate.recommended_interval_hours));
    setMode('auto');
  };

  const persistSettings = async (overrides = {}) => {
    const payload = {
      scenario_type: scenarioType,
      offer_ids: scenarioType === 'manual' ? offerIds : [],
      region,
      mode,
      schedule_json: mode === 'auto' ? schedule : null,
      is_active: isActive,
      executor: 'windows_agent',
      custom_source: scenarioType === 'custom' ? customSource : null,
      custom_value: scenarioType === 'custom' ? customValue : null,
      workspace_id: workspaceId || null,
      ...overrides,
    };
    return await vitrineSaveSettings({ request, accountId, payload });
  };

  const saveAssortment = async () => {
    setSaving(true);
    try {
      await persistSettings();
      showStatus('success', 'Сохранено');
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setSaving(false);
    }
  };

  const openManualConfirm = async () => {
    setPreviewLoading(true);
    try {
      // Сохраняем ассортимент для проверки, но расписание оставляем как было:
      // разовая проверка не должна молча выключать автозапуск.
      await persistSettings({ mode: isActive ? 'auto' : 'manual', is_active: isActive });
      const preview = await vitrineManualCostPreview({ request, accountId, workspaceId });
      setConfirm({ type: 'manual', preview });
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const openAutoConfirm = async () => {
    setPreviewLoading(true);
    try {
      // Настройки сохраняются только по «Включить» в окне подтверждения: раньше здесь
      // сохранялось is_active=false, и «Отмена» оставляла автопроверку выключенной.
      const preview = await vitrineAutoCostPreview({ request, accountId, workspaceId, scheduleJson: schedule });
      setConfirm({ type: 'auto', preview });
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmManualRun = async () => {
    setConfirmBusy(true);
    try {
      await onRun();
      setConfirm(null);
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const confirmAutoEnable = async () => {
    setConfirmBusy(true);
    try {
      await persistSettings({ mode: 'auto', is_active: true });
      setIsActive(true);
      showStatus('success', 'Автозапуск включён');
      setConfirm(null);
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    } finally {
      setConfirmBusy(false);
    }
  };

  const disableAuto = async () => {
    try {
      await persistSettings({ is_active: false });
      setIsActive(false);
      showStatus('success', 'Автозапуск выключен');
    } catch (e) {
      showStatus('error', 'Ошибка: ' + e.message);
    }
  };

  if (loading) return <div className="vm-cfg vm-cfg--loading">Загрузка настроек...</div>;

  return (
    <div className="vm-cfg">
      <div className="vm-cfg__step">
        <div className="vm-cfg__step-title">1. Где выполнять проверку</div>
        <div className="vm-cfg__toggle">
          <button
            className={`vm-cfg__toggle-btn${executor === 'windows_agent' ? ' vm-cfg__toggle-btn--on' : ''}`}
            onClick={() => { setExecutor('windows_agent'); setScenarioType('all'); }}
          >
            На моём компьютере — бесплатно
          </button>
        </div>
        <div className="vm-cfg__hint">
          Задание заберёт «Энилекс.Контроль Локальный агент». Используется одна сохранённая сессия Ozon и выбранный в ней регион.
        </div>
        {legacyParserMarket && (
          <div className="vm-cfg__hint vm-cfg__hint--warn">
            Раньше проверка шла на сервере parser.market — этот исполнитель отключён.
            После «Сохранить» мониторинг перейдёт на локальный агент, а проверки parser.market остановятся.
          </div>
        )}
        {executor === 'windows_agent' && <LocalAgentSetup />}
      </div>

      <div className="vm-cfg__step">
        <div className="vm-cfg__step-title">2. Ассортимент</div>
        <div className="vm-cfg__toggle">
          <button
            className={`vm-cfg__toggle-btn${scenarioType === 'all' ? ' vm-cfg__toggle-btn--on' : ''}`}
            onClick={() => setScenarioType('all')}
          >
            Весь ассортимент
          </button>
          <button
            className={`vm-cfg__toggle-btn${scenarioType === 'manual' ? ' vm-cfg__toggle-btn--on' : ''}`}
            onClick={() => setScenarioType('manual')}
            disabled={executor === 'windows_agent'}
          >
            Список артикулов
          </button>
          <button
            className={`vm-cfg__toggle-btn${scenarioType === 'custom' ? ' vm-cfg__toggle-btn--on' : ''}`}
            onClick={() => setScenarioType('custom')}
          >
            Своё задание
          </button>
        </div>

        {scenarioType === 'custom' && (
          <div className="vm-cfg__row">
            <label className="vm-cfg__label">Источник</label>
            <select className="vm-cfg__select" value={customSource} onChange={e => setCustomSource(e.target.value)}>
              <option value="url">Ссылка на поисковую выдачу</option>
              <option value="shop">Ссылка на магазин</option>
              <option value="sku_list">Список SKU</option>
            </select>
            <textarea
              className="vm-paste__area"
              value={customValue}
              onChange={e => setCustomValue(e.target.value)}
              rows={3}
              placeholder={customSource === 'sku_list' ? 'SKU — по одному в строке, через запятую или точку с запятой' : 'Вставьте ссылку Ozon'}
            />
          </div>
        )}

        {scenarioType === 'all' && (
          <div className="vm-cfg__hint">
            Агент проходит выдачу магазина по Seller ID до блока «Возможно, вам понравится». Повторный обход каждой карточки не нужен.
          </div>
        )}

        {scenarioType === 'manual' && (
          <>
            <PasteBox onApply={onPasteApply} onClearAll={onClearAll} />
            <div className="vm-cfg__hint">
              Выбрано товаров: <strong>{selectedCount}</strong> — отмечайте галочками в таблице «Витринные цены» ниже.
              Выбор сохраняется даже если товара сейчас нет в наличии — проверяться будут только те из выбранных, у кого на момент запуска есть остатки.
            </div>
          </>
        )}

        <div className="vm-cfg__save-row">
          <button className="btn btn--primary btn--sm" onClick={saveAssortment} disabled={saving}>
            {saving ? 'Сохранение...' : 'Сохранить настройки'}
          </button>
        </div>
      </div>

      <div className="vm-cfg__step">
        <div className="vm-cfg__step-title">3. Режим запуска</div>
        <div className="vm-cfg__toggle">
          <button
            className={`vm-cfg__toggle-btn${mode === 'manual' ? ' vm-cfg__toggle-btn--on' : ''}`}
            onClick={() => setMode('manual')}
          >
            Ручной
          </button>
          <button
            className={`vm-cfg__toggle-btn${mode === 'auto' ? ' vm-cfg__toggle-btn--on' : ''}`}
            onClick={() => setMode('auto')}
          >
            Автоматический
          </button>
        </div>

        {executor === 'windows_agent' && timeEstimate && (
          <div className="vm-cfg__time-estimate">
            <div>
              <span className="vm-cfg__time-label">Примерное время обхода</span>
              <strong>{formatDuration(timeEstimate.estimated_minutes)}</strong>
              <span className="vm-cfg__time-meta">
                {timeEstimate.offer_count} товаров в продаже · рекомендуемый максимум — раз в {timeEstimate.recommended_interval_hours} ч
              </span>
            </div>
            <button className="btn btn--secondary btn--sm" onClick={applyRecommendedSchedule}>
              Рекомендуемое расписание
            </button>
            <p className="vm-cfg__time-warning">
              Если локальный агент обходит несколько магазинов, учитывайте их общую очередь и уменьшайте частоту каждого кабинета: рекомендации рассчитаны для одного магазина.
            </p>
          </div>
        )}

        {mode === 'manual' && (
          <div className="vm-cfg__manual">
            <button
              className="btn btn--primary btn--sm"
              onClick={openManualConfirm}
              disabled={loadingCheck || previewLoading}
            >
              {loadingCheck ? 'Запрос...' : previewLoading ? 'Подготавливаем...' : 'Запустить проверку'}
            </button>
          </div>
        )}

        {mode === 'auto' && (
          <div className="vm-cfg__auto">
            <ScheduleGrid value={schedule} onChange={setSchedule} />
            <div className="vm-cfg__auto-actions">
              {isActive ? (
                <>
                  <span className="vm-cfg__auto-status vm-cfg__auto-status--on">● Автозапуск включён</span>
                  <button className="btn btn--ghost btn--sm" onClick={disableAuto}>Выключить</button>
                  <button className="btn btn--ghost btn--sm" onClick={openAutoConfirm} disabled={previewLoading}>
                  {previewLoading ? '...' : 'Обновить расписание'}
                  </button>
                </>
              ) : (
                <button className="btn btn--primary btn--sm" onClick={openAutoConfirm} disabled={previewLoading}>
                  {previewLoading ? 'Подготавливаем...' : 'Сохранить и включить'}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {confirm?.type === 'manual' && (
        <ConfirmModal
          title="Подтвердите запуск проверки"
          lines={[
            'Будет поставлен обход всего магазина локальным агентом.',
            'Стоимость: 0 ₽. Если агент сейчас выключен, задача дождётся его запуска.',
          ]}
          confirmLabel="Запустить"
          onConfirm={confirmManualRun}
          onCancel={() => setConfirm(null)}
          loading={confirmBusy}
        />
      )}
      {confirm?.type === 'auto' && (
        <ConfirmModal
          title="Подтвердите включение автозапуска"
          lines={[
            'По расписанию будет обходиться весь магазин через локальный агент.',
            `Проверок в среднем в день: ${confirm.preview.checks_per_day}`,
            'Стоимость: 0 ₽. Пропущенные проверки не создают искусственную историю и объединяются в одну актуальную задачу.',
            'Для выполнения компьютер и локальный агент должны быть включены.',
          ]}
          confirmLabel="Включить"
          onConfirm={confirmAutoEnable}
          onCancel={() => setConfirm(null)}
          loading={confirmBusy}
        />
      )}
    </div>
  );
}

// ── Coinvest Dashboard ────────────────────────────────────────────────────

// `field` is 'by_task' for single checks (/prices/pivot) or 'by_col' for the
// grouped history, where a period cell already holds the item's average.
function calcPeriodStats(items, periodId, field = 'by_task') {
  const snaps = items
    .map(item => item[field]?.[periodId])
    .filter(s =>
      s &&
      s.coinvest_pct != null &&
      s.seller_price != null &&
      s.vitrine_price != null &&
      Math.abs(Number(s.seller_price) - Number(s.vitrine_price)) > 0.5
    );
  if (!snaps.length) return null;

  const vals = snaps.map(s => Number(s.coinvest_pct)).sort((a, b) => a - b);
  const n = vals.length;
  const mean = vals.reduce((a, b) => a + b, 0) / n;
  const median = n % 2 === 0 ? (vals[n / 2 - 1] + vals[n / 2]) / 2 : vals[Math.floor(n / 2)];
  return { min: vals[0], max: vals[n - 1], mean, median, count: n };
}

function fmtPct(v) {
  if (v == null) return '—';
  return `${Number(v).toFixed(1)}%`;
}

function Delta({ val }) {
  if (val == null) return null;
  const pos = val >= 0;
  return (
    <span className={`cv-stat__delta ${pos ? 'cv-stat__delta--up' : 'cv-stat__delta--dn'}`}>
      {pos ? '▲' : '▼'} {Math.abs(val).toFixed(1)}%
    </span>
  );
}

const TREND_WEEKS = 8;

function CoinvestDashboard({ pivot, weekly }) {
  const items = pivot?.items;
  const periods = pivot?.periods;
  if (!items?.length || !periods?.length) return null;

  // `periods` is oldest→newest — the newest check is the LAST element.
  const lastPeriod = periods[periods.length - 1];
  const latest = { period: lastPeriod, stats: calcPeriodStats(items, lastPeriod.id) };
  if (!latest.stats) return null;

  // A day can hold 24 checks, so check-by-check bars are noise. The trend is
  // weekly averages (same weeks as the price table), the delta on the cards
  // compares the current state with the previous week's average.
  const weeks = (weekly?.columns || [])
    .map(col => ({ col, stats: calcPeriodStats(weekly.items || [], col.id, 'by_col') }))
    .filter(x => x.stats);
  const prevWeek = weeks[weeks.length - 2];
  const delta = (key) => prevWeek ? latest.stats[key] - prevWeek.stats[key] : null;
  const trend = weeks.slice(-TREND_WEEKS).reverse();
  const currentWeekId = weeks[weeks.length - 1]?.col.id;
  const maxMean = Math.max(...trend.map(x => x.stats.mean));

  const STATS = [
    { label: 'Минимум', key: 'min' },
    { label: 'Максимум', key: 'max' },
    { label: 'Среднее', key: 'mean' },
    { label: 'Медиана', key: 'median' },
  ];

  return (
    <div className="cv">
      <div className="cv__header">
        <span className="cv__title">Соинвест Ozon</span>
        <span className="cv__sub">
          Участвуют <strong>{latest.stats.count}</strong> из <strong>{items.length}</strong> товаров
          &nbsp;·&nbsp; последний замер {fmtDT(latest.period.created_at)}
          &nbsp;·&nbsp; исключены товары, где тек. цена = цена без карты
        </span>
      </div>
      <div className="cv__body">
        <div className="cv__stats">
          {STATS.map(({ label, key }) => (
            <div key={key} className="cv-stat" title={prevWeek ? `Изменение к среднему за ${prevWeek.col.label}` : undefined}>
              <span className="cv-stat__label">{label}</span>
              <span className="cv-stat__val">{fmtPct(latest.stats[key])}</span>
              <Delta val={delta(key)} />
            </div>
          ))}
        </div>
        {trend.length > 1 && (
          <div className="cv__trend">
            <div className="cv__trend-title">Динамика среднего по неделям</div>
            {trend.map(({ col, stats }) => {
              const barW = maxMean > 0 ? (stats.mean / maxMean) * 100 : 0;
              const isCurrent = col.id === currentWeekId;
              return (
                <div key={col.id} className="cv-bar">
                  <span className="cv-bar__dt">{col.label}</span>
                  <div className="cv-bar__track">
                    <div className={`cv-bar__fill${isCurrent ? ' cv-bar__fill--latest' : ''}`} style={{ width: `${barW}%` }} />
                  </div>
                  <span className="cv-bar__val">{fmtPct(stats.mean)}</span>
                  {isCurrent && <span className="cv-bar__now">← текущая</span>}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Coinvest Dynamics Table ───────────────────────────────────────────────
// Same period tree as «Витринные цены»: closed months are one row, the
// current month goes by weeks/days, today by checks. A row with ⊞ drills one
// level deeper, a ⊟ group row folds it back. Every period row is the
// coinvest distribution across items, each item taken at its period average.

function periodTitle(col) {
  return [...(col.path || []).map(c => c.label), col.label].join(' → ');
}

function downloadCoinvestCsv(rows, totalItems) {
  const headers = ['Период', 'Тип', 'Город', 'Собрал', `Участвуют (из ${totalItems})`, 'Мин %', 'Макс %', 'Среднее %', 'Δ Среднее', 'Медиана %'];
  const data = rows.map(({ col, stats, deltaMean }) => [
    periodTitle(col),
    KIND_HINT[col.kind] || '',
    col.region || '',
    [col.collector_name, col.device_name].filter(Boolean).join(' · '),
    stats?.count ?? '',
    stats ? Number(stats.min).toFixed(1) : '',
    stats ? Number(stats.max).toFixed(1) : '',
    stats ? Number(stats.mean).toFixed(1) : '',
    deltaMean != null ? (deltaMean >= 0 ? '+' : '') + Number(deltaMean).toFixed(1) : '',
    stats ? Number(stats.median).toFixed(1) : '',
  ]);
  const csv = [headers, ...data].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = 'coinvest_dynamics.csv'; a.click();
  URL.revokeObjectURL(url);
}

function CoinvestDynamicsTable({ accountId }) {
  const { request, showStatus } = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [months, setMonths] = useState(6);
  const [expand, setExpand] = useState(new Set());
  const [collapse, setCollapse] = useState(new Set());

  useEffect(() => {
    setLoading(true);
    vitrineGetGroupedPivot({ request, accountId, months, expand: [...expand], collapse: [...collapse] })
      .then(setData)
      .catch(() => showStatus('error', 'Ошибка загрузки'))
      .finally(() => setLoading(false));
  }, [accountId, months, expand, collapse]); // eslint-disable-line

  const expandNode = (id) => {
    setExpand(prev => new Set(prev).add(id));
    setCollapse(prev => { const n = new Set(prev); n.delete(id); return n; });
  };
  const collapseNode = (id) => {
    setCollapse(prev => new Set(prev).add(id));
    setExpand(prev => { const n = new Set(prev); n.delete(id); return n; });
  };

  if (loading && !data) return <div className="vm__empty">Загрузка...</div>;
  if (!data?.columns?.length) return <div className="vm__empty">Нет данных — сделайте первую проверку цен</div>;

  const items = data.items || [];
  const rows = [];
  let prevMean = null;
  for (const col of data.columns) {
    const stats = calcPeriodStats(items, col.id, 'by_col');
    const deltaMean = stats && prevMean != null ? stats.mean - prevMean : null;
    if (stats) prevMean = stats.mean;
    rows.push({ col, stats, deltaMean });
  }

  // Group rows above a run of columns that share an open ancestor — clicking
  // one folds the whole run back into that period's average.
  const body = [];
  let prevPath = [];
  rows.forEach((row, i) => {
    const path = row.col.path || [];
    let same = 0;
    while (same < path.length && same < prevPath.length && path[same].id === prevPath[same].id) same += 1;
    for (let level = same; level < path.length; level += 1) {
      const crumb = path[level];
      body.push(
        <tr key={`g-${crumb.id}`} className="cv-dyn__row cv-dyn__row--group" onClick={() => collapseNode(crumb.id)}
          title="Свернуть обратно в средний показатель периода">
          <td className="cv-dyn__td cv-dyn__td--group" colSpan={7} style={{ paddingLeft: 14 + level * 18 }}>
            ⊟ {crumb.label}
          </td>
        </tr>,
      );
    }
    prevPath = path;

    const { col, stats, deltaMean } = row;
    const isLatest = i === rows.length - 1;
    const na = <span className="vm-pivot__na">—</span>;
    body.push(
      <tr key={col.id} className={`cv-dyn__row${isLatest ? ' cv-dyn__row--latest' : ''}`}>
        <td
          className={`cv-dyn__td cv-dyn__td--dt${col.has_children ? ' cv-dyn__td--drill' : ''}`}
          style={{ paddingLeft: 14 + path.length * 18 }}
          onClick={col.has_children ? () => expandNode(col.id) : undefined}
          title={col.has_children ? 'Раскрыть период' : undefined}
        >
          <span className="cv-dyn__period">{col.has_children ? '⊞ ' : ''}{col.label}</span>
          <span className="cv-dyn__kind">
            {col.kind === 'check'
              ? [col.region, col.collector_name, col.device_name].filter(Boolean).join(' · ') || KIND_HINT.check
              : KIND_HINT[col.kind]}
          </span>
        </td>
        <td className="cv-dyn__td cv-dyn__td--num">{stats ? `${stats.count} / ${items.length}` : na}</td>
        <td className="cv-dyn__td cv-dyn__td--num">{stats ? fmtPct(stats.min) : na}</td>
        <td className="cv-dyn__td cv-dyn__td--num">{stats ? fmtPct(stats.max) : na}</td>
        <td className="cv-dyn__td cv-dyn__td--num cv-dyn__td--mean">{stats ? fmtPct(stats.mean) : na}</td>
        <td className="cv-dyn__td cv-dyn__td--num">
          {deltaMean != null ? (
            <span className={deltaMean >= 0 ? 'cv-dyn__delta--up' : 'cv-dyn__delta--dn'}>
              {deltaMean >= 0 ? '▲' : '▼'} {Math.abs(deltaMean).toFixed(1)}%
            </span>
          ) : na}
        </td>
        <td className="cv-dyn__td cv-dyn__td--num">{stats ? fmtPct(stats.median) : na}</td>
      </tr>,
    );
  });

  return (
    <div className="cv-dyn">
      <div className="cv-dyn__toolbar">
        <span className="cv-dyn__hint">
          Закрытый период — среднее по его замерам · исключены товары, где тек. цена = цена без карты · {items.length} товаров всего
        </span>
        <div className="cv-dyn__actions">
          <label className="vm-pivot__months">
            История
            <select value={months} onChange={e => setMonths(Number(e.target.value))}>
              <option value={3}>3 мес.</option>
              <option value={6}>6 мес.</option>
              <option value={12}>12 мес.</option>
              <option value={24}>24 мес.</option>
            </select>
          </label>
          <button className="btn btn--ghost btn--sm" onClick={() => downloadCoinvestCsv(rows, items.length)}>
            ↓ Скачать CSV
          </button>
        </div>
      </div>
      <div className="cv-dyn__wrap">
        <table className="cv-dyn__table">
          <thead>
            <tr>
              <th className="cv-dyn__th">Период</th>
              <th className="cv-dyn__th cv-dyn__th--num">Участвуют</th>
              <th className="cv-dyn__th cv-dyn__th--num">Мин</th>
              <th className="cv-dyn__th cv-dyn__th--num">Макс</th>
              <th className="cv-dyn__th cv-dyn__th--num">Среднее</th>
              <th className="cv-dyn__th cv-dyn__th--num">Δ Среднее</th>
              <th className="cv-dyn__th cv-dyn__th--num">Медиана</th>
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
    </div>
  );
}

// ── Pivot Table ───────────────────────────────────────────────────────────

function colTitle(col) {
  const crumbs = [...(col.path || []).map(c => c.label), col.label];
  const extra = [col.region, col.scenario_name, col.collector_name, col.device_name].filter(Boolean);
  return crumbs.join(' → ') + (extra.length ? ` (${extra.join(' · ')})` : '');
}

function downloadPivotCsv(columns, items) {
  const headers = ['Артикул', 'SKU', 'Название', 'Остаток'];
  for (const col of columns) {
    const t = colTitle(col);
    headers.push(`${t} Цена продавца`);
    headers.push(`${t} С картой`);
    headers.push(`${t} Цена без карты`);
    headers.push(`${t} Соинвест`);
  }
  const rows = [headers];
  for (const item of items) {
    const row = [item.offer_id, item.sku || '', item.name || '', item.stock ?? ''];
    for (const col of columns) {
      const cell = item.by_col?.[col.id];
      row.push(cell?.seller_price != null ? Number(cell.seller_price).toFixed(2) : '');
      row.push(cell?.promo_price != null ? Number(cell.promo_price).toFixed(2) : '');
      row.push(cell?.vitrine_price != null ? Number(cell.vitrine_price).toFixed(2) : '');
      row.push(cell?.coinvest_pct != null ? `-${Number(cell.coinvest_pct).toFixed(1)}%` : '');
    }
    rows.push(row);
  }
  const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(';')).join('\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = 'vitrine_prices.csv'; a.click();
  URL.revokeObjectURL(url);
}

function useMergedRows(grouped, allProducts) {
  return useMemo(() => {
    const stock = grouped?.stock || {};
    const byOffer = new Map();
    for (const p of allProducts || []) {
      byOffer.set(p.offer_id, { offer_id: p.offer_id, sku: p.sku || null, name: p.name || null, by_col: {} });
    }
    for (const item of grouped?.items || []) {
      const existing = byOffer.get(item.offer_id);
      if (existing) {
        existing.sku = item.sku || existing.sku;
        existing.name = item.name || existing.name;
        existing.by_col = item.by_col || {};
      } else {
        byOffer.set(item.offer_id, { ...item, by_col: item.by_col || {} });
      }
    }
    return Array.from(byOffer.values())
      .map(row => ({ ...row, stock: stock[row.offer_id] ?? null }))
      .sort((a, b) => a.offer_id.localeCompare(b.offer_id));
  }, [grouped, allProducts]);
}

// Grouped header: one row per open level, so each averaged period keeps its
// parent above it and can be folded back by clicking that parent.
function buildHeaderLevels(columns) {
  const depth = columns.reduce((max, c) => Math.max(max, (c.path || []).length), 0);
  const levels = [];
  for (let level = 0; level < depth; level++) {
    const cells = [];
    let i = 0;
    while (i < columns.length) {
      const crumb = (columns[i].path || [])[level];
      if (!crumb) {
        cells.push({ key: `pad-${level}-${i}`, span: 1 });
        i += 1;
        continue;
      }
      let j = i;
      while (j < columns.length && ((columns[j].path || [])[level] || {}).id === crumb.id) j += 1;
      cells.push({ key: `${level}-${crumb.id}`, crumb, span: j - i });
      i = j;
    }
    levels.push(cells);
  }
  return levels;
}

const KIND_HINT = {
  month: 'среднее за месяц',
  week: 'среднее за неделю',
  day: 'среднее за день',
  check: 'отдельный замер',
};

function PivotTable({
  grouped, loading, allProducts,
  selectable, selected, onToggle, onToggleMany,
  onExpand, onCollapse, months, onMonthsChange,
}) {
  const [filter, setFilter] = useState('all'); // 'all' | 'monitored'
  const [search, setSearch] = useState('');
  const [inStockOnly, setInStockOnly] = useState(false);
  const [page, setPage] = useState(0);

  const rows = useMergedRows(grouped, allProducts);

  const filtered = useMemo(() => {
    let list = rows;
    if (selectable && filter === 'monitored') list = list.filter(r => selected.has(r.offer_id));
    if (inStockOnly) list = list.filter(r => Number(r.stock || 0) > 0);
    if (search.trim()) {
      list = filterBySearch(list, search);
    }
    return list;
  }, [rows, filter, search, inStockOnly, selectable, selected]);

  useEffect(() => { setPage(0); }, [filter, search, inStockOnly, grouped]);

  if (loading) return <div className="vm__empty">Загрузка...</div>;
  if (!rows.length) return <div className="vm__empty">Нет товаров в ассортименте</div>;

  const columns = grouped?.columns || [];
  const theadRows = [
    ...buildHeaderLevels(columns).map((cells, i) => ({ key: `group-${i}`, kind: 'group', cells })),
    { key: 'columns', kind: 'columns' },
    { key: 'metrics', kind: 'metrics' },
  ];
  const fixedRowSpan = theadRows.length;
  const totalPages = Math.max(1, Math.ceil(filtered.length / PIVOT_PAGE_SIZE));
  const pageItems = filtered.slice(page * PIVOT_PAGE_SIZE, (page + 1) * PIVOT_PAGE_SIZE);
  const allPageChecked = selectable && pageItems.length > 0 && pageItems.every(r => selected.has(r.offer_id));
  const latestCol = columns[columns.length - 1];

  return (
    <div className="vm-pivot__outer">
      <div className="vm-pivot__topbar">
        {selectable && (
          <div className="vm-atable__filters">
            <button
              className={`btn btn--ghost btn--xs${filter === 'all' ? ' btn--active' : ''}`}
              onClick={() => setFilter('all')}
            >
              Все товары
            </button>
            <button
              className={`btn btn--ghost btn--xs${filter === 'monitored' ? ' btn--active' : ''}`}
              onClick={() => setFilter('monitored')}
            >
              В мониторинге ({selected.size})
            </button>
          </div>
        )}
        <label className="vm-pivot__stock-toggle" title="Остатки FBS + FBO из последней синхронизации">
          <input type="checkbox" checked={inStockOnly} onChange={e => setInStockOnly(e.target.checked)} />
          Только товары с остатками
        </label>
        <label className="vm-pivot__months">
          История
          <select value={months} onChange={e => onMonthsChange(Number(e.target.value))}>
            <option value={3}>3 мес.</option>
            <option value={6}>6 мес.</option>
            <option value={12}>12 мес.</option>
            <option value={24}>24 мес.</option>
          </select>
        </label>
        <input
          className="vm-atable__search"
          placeholder="Поиск по артикулу или названию"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        {columns.length > 0 && (
          <button className="btn btn--ghost btn--sm" onClick={() => downloadPivotCsv(columns, filtered)}>
            ↓ Скачать CSV
          </button>
        )}
      </div>
      <div className="vm-pivot__wrap">
        <table className={`vm-pivot${selectable ? ' vm-pivot--selectable' : ''}`}>
          <thead>
            {theadRows.map((row, rowIdx) => (
              <tr key={row.key}>
                {rowIdx === 0 && (
                  <>
                    {selectable && (
                      <th className="vm-pivot__th vm-pivot__th--fix vm-pivot__th--chk" rowSpan={fixedRowSpan}>
                        <input
                          type="checkbox"
                          checked={allPageChecked}
                          onChange={() => onToggleMany(pageItems.map(r => r.offer_id), !allPageChecked)}
                        />
                      </th>
                    )}
                    <th className="vm-pivot__th vm-pivot__th--fix vm-pivot__th--f1" rowSpan={fixedRowSpan}>Артикул</th>
                    <th className="vm-pivot__th vm-pivot__th--fix vm-pivot__th--f2" rowSpan={fixedRowSpan}>SKU</th>
                    <th className="vm-pivot__th vm-pivot__th--fix vm-pivot__th--f3" rowSpan={fixedRowSpan}>Название</th>
                    <th className="vm-pivot__th vm-pivot__th--fix vm-pivot__th--f4" rowSpan={fixedRowSpan}>Тек. цена</th>
                    <th className="vm-pivot__th vm-pivot__th--stock" rowSpan={fixedRowSpan}>Остаток</th>
                  </>
                )}

                {row.kind === 'group' && row.cells.map(cell => (
                  cell.crumb ? (
                    <th
                      key={cell.key}
                      className="vm-pivot__th vm-pivot__th--group"
                      colSpan={cell.span * 4}
                      onClick={() => onCollapse(cell.crumb.id)}
                      title="Свернуть обратно в средний показатель периода"
                    >
                      ⊟ {cell.crumb.label}
                    </th>
                  ) : <th key={cell.key} className="vm-pivot__th vm-pivot__th--group-pad" colSpan={cell.span * 4} />
                ))}

                {row.kind === 'columns' && (columns.length === 0
                  ? <th className="vm-pivot__th" rowSpan={2}>Проверок ещё не было</th>
                  : columns.map(col => (
                    <th
                      key={col.id}
                      className={`vm-pivot__th vm-pivot__th--period-hdr${col.has_children ? ' vm-pivot__th--drill' : ''}`}
                      colSpan={4}
                      onClick={col.has_children ? () => onExpand(col.id) : undefined}
                      title={col.has_children ? 'Раскрыть период' : KIND_HINT[col.kind]}
                    >
                      <span className="vm-pivot__period-dt">
                        {col.has_children ? '⊞ ' : ''}{col.label}
                      </span>
                      {col.kind === 'check' && col.region && (
                        <span className="vm-pivot__period-region">{col.region}</span>
                      )}
                      {col.kind === 'check' && (col.collector_name || col.device_name) && (
                        <span className="vm-pivot__period-scenario">
                          {[col.collector_name, col.device_name].filter(Boolean).join(' · ')}
                        </span>
                      )}
                      {col.kind !== 'check' && (
                        <span className="vm-pivot__period-region">{KIND_HINT[col.kind]}</span>
                      )}
                    </th>
                  )))}

                {row.kind === 'metrics' && columns.flatMap(col => [
                  <th key={`${col.id}-s`} className="vm-pivot__th vm-pivot__th--sub" title="Цена продавца, зафиксированная на момент замера">Цена продавца</th>,
                  <th key={`${col.id}-g`} className="vm-pivot__th vm-pivot__th--sub vm-pivot__th--sub-green">С картой</th>,
                  <th key={`${col.id}-b`} className="vm-pivot__th vm-pivot__th--sub vm-pivot__th--sub-black">Цена без карты</th>,
                  <th key={`${col.id}-c`} className="vm-pivot__th vm-pivot__th--sub vm-pivot__th--sub-coin">Соинвест</th>,
                ])}
              </tr>
            ))}
          </thead>
          <tbody>
            {pageItems.map(item => {
              const skuUrl = item.sku ? `https://www.ozon.ru/product/${item.sku}` : null;
              return (
                <tr key={item.offer_id} className="vm-pivot__row">
                  {selectable && (
                    <td className="vm-pivot__td vm-pivot__td--fix vm-pivot__td--chk">
                      <input
                        type="checkbox"
                        checked={selected.has(item.offer_id)}
                        onChange={() => onToggle(item.offer_id)}
                      />
                    </td>
                  )}
                  <td className="vm-pivot__td vm-pivot__td--fix vm-pivot__td--f1">{item.offer_id}</td>
                  <td className="vm-pivot__td vm-pivot__td--fix vm-pivot__td--f2">
                    {skuUrl
                      ? <a href={skuUrl} target="_blank" rel="noreferrer" className="vm-pivot__sku-link">{item.sku}</a>
                      : item.sku || '—'}
                  </td>
                  <td className="vm-pivot__td vm-pivot__td--fix vm-pivot__td--f3">{item.name || '—'}</td>
                  <td className="vm-pivot__td vm-pivot__td--fix vm-pivot__td--f4">
                    {(() => {
                      // Columns run oldest→newest, so the current seller price is
                      // the newest column's — never the leftmost one.
                      const sp = item.by_col?.[latestCol?.id]?.seller_price;
                      return sp != null ? fmtPrice(sp) : <span className="vm-pivot__na">—</span>;
                    })()}
                  </td>
                  <td className="vm-pivot__td vm-pivot__td--stock">
                    {item.stock == null ? <span className="vm-pivot__na">—</span> : item.stock}
                  </td>
                  {columns.flatMap(col => {
                    const cell = item.by_col?.[col.id];
                    return [
                      <td key={`${col.id}-s`} className="vm-pivot__td vm-pivot__td--price">
                        {cell?.seller_price != null ? fmtPrice(cell.seller_price) : <span className="vm-pivot__na">—</span>}
                      </td>,
                      <td key={`${col.id}-g`} className="vm-pivot__td vm-pivot__td--price vm-pivot__td--green">
                        {cell?.promo_price != null ? fmtPrice(cell.promo_price) : <span className="vm-pivot__na">—</span>}
                      </td>,
                      <td key={`${col.id}-b`} className="vm-pivot__td vm-pivot__td--price vm-pivot__td--black">
                        {cell?.vitrine_price != null ? fmtPrice(cell.vitrine_price) : <span className="vm-pivot__na">—</span>}
                      </td>,
                      <td key={`${col.id}-c`} className="vm-pivot__td vm-pivot__td--coinvest-cell">
                        {cell?.coinvest_pct != null
                          ? <span className="vm-pivot__coinvest">{fmtCoinvest(cell.coinvest_pct)}</span>
                          : <span className="vm-pivot__na">—</span>}
                      </td>,
                    ];
                  })}
                  {columns.length === 0 && <td className="vm-pivot__td" />}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {totalPages > 1 && (
        <div className="vm-atable__pager">
          <button className="btn btn--ghost btn--xs" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
            ← Назад
          </button>
          <span className="vm-atable__pageinfo">{page + 1} / {totalPages}</span>
          <button
            className="btn btn--ghost btn--xs"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(p => p + 1)}
          >
            Далее →
          </button>
        </div>
      )}
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────

export default function OzonVitrineMonitor({ accountId, workspaceId }) {
  const { request, showStatus } = useApi();

  const [tab, setTab] = useState('prices');
  const [pivot, setPivot] = useState(null);
  const [weekly, setWeekly] = useState(null);
  const [loadingPivot, setLoadingPivot] = useState(false);
  const [grouped, setGrouped] = useState(null);
  const [loadingGrouped, setLoadingGrouped] = useState(false);
  const [months, setMonths] = useState(6);
  // Explicit overrides of the server's default open/closed state per period
  // node — everything else follows "current month open, today down to checks".
  const [expand, setExpand] = useState(new Set());
  const [collapse, setCollapse] = useState(new Set());
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [recentTasks, setRecentTasks] = useState([]);
  const [retryingTaskId, setRetryingTaskId] = useState(null);
  const [cancellingTaskId, setCancellingTaskId] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const pollRef = useRef(null);

  // Monitor config — single source of truth for both the step 1/2 wizard and
  // the checkbox column in the price table below.
  const [cfgLoading, setCfgLoading] = useState(true);
  // parser.market отключён: проверку выполняет только локальный агент.
  const [executor, setExecutor] = useState('windows_agent');
  const [legacyParserMarket, setLegacyParserMarket] = useState(false);
  const [scenarioType, setScenarioType] = useState('all');
  const [customSource, setCustomSource] = useState('url');
  const [customValue, setCustomValue] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [region, setRegion] = useState('Москва');
  const [mode, setMode] = useState('manual');
  const [schedule, setSchedule] = useState(emptySchedule());
  const [isActive, setIsActive] = useState(false);

  const toggleOffer = (id) => setSelected(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const toggleOffers = (ids, checked) => setSelected(prev => {
    const n = new Set(prev);
    ids.forEach(id => checked ? n.add(id) : n.delete(id));
    return n;
  });

  const knownOfferIds = useMemo(() => new Set(allProducts.map(p => p.offer_id)), [allProducts]);

  const handlePasteApply = (tokens) => {
    const matched = [];
    const unmatched = [];
    for (const t of tokens) (knownOfferIds.has(t) ? matched : unmatched).push(t);
    if (matched.length) toggleOffers(matched, true);
    if (unmatched.length) {
      showStatus(
        matched.length ? 'info' : 'error',
        matched.length
          ? `Добавлено ${matched.length} — не найдено в ассортименте: ${unmatched.join(', ')}`
          : `Ничего не найдено в ассортименте: ${unmatched.join(', ')}`,
      );
    } else if (matched.length) {
      showStatus('success', `Добавлено ${matched.length}`);
    }
  };

  // Prune any stale/garbage offer_ids from selection once the real assortment
  // is known — e.g. an already-saved selection that no longer matches a real
  // product, so counts and the "В мониторинге" filter stay trustworthy.
  useEffect(() => {
    if (!allProducts.length) return;
    setSelected(prev => {
      const cleaned = new Set([...prev].filter(id => knownOfferIds.has(id)));
      return cleaned.size === prev.size ? prev : cleaned;
    });
  }, [allProducts]); // eslint-disable-line

  const loadPivot = useCallback(async () => {
    setLoadingPivot(true);
    try {
      const [data, weeks] = await Promise.all([
        vitrineGetPricePivot({ request, accountId, limit: 1 }),
        vitrineGetGroupedPivot({ request, accountId, months: 3, level: 'week' }),
      ]);
      setPivot(data);
      setWeekly(weeks);
    } catch (e) {
      showStatus('error', 'Ошибка загрузки цен: ' + e.message);
    } finally {
      setLoadingPivot(false);
    }
  }, [request, accountId]); // eslint-disable-line

  const loadGrouped = useCallback(async () => {
    setLoadingGrouped(true);
    try {
      const data = await vitrineGetGroupedPivot({
        request, accountId, months,
        expand: [...expand], collapse: [...collapse],
      });
      setGrouped(data);
    } catch (e) {
      showStatus('error', 'Ошибка загрузки истории цен: ' + e.message);
    } finally {
      setLoadingGrouped(false);
    }
  }, [request, accountId, months, expand, collapse]); // eslint-disable-line

  const expandNode = (nodeId) => {
    setExpand(prev => new Set(prev).add(nodeId));
    setCollapse(prev => {
      if (!prev.has(nodeId)) return prev;
      const next = new Set(prev); next.delete(nodeId); return next;
    });
  };

  const collapseNode = (nodeId) => {
    setCollapse(prev => new Set(prev).add(nodeId));
    setExpand(prev => {
      if (!prev.has(nodeId)) return prev;
      const next = new Set(prev); next.delete(nodeId); return next;
    });
  };

  const loadTasks = useCallback(async () => {
    const tasks = await vitrineListTasks({ request, accountId, workspaceId });
    setRecentTasks(tasks);
    const pending = tasks.find(t => t.status === 'pending' || t.status === 'processing');
    if (pending) {
      setActiveTask(pending);
      setLoadingCheck(true);
    }
    return tasks;
  }, [request, accountId, workspaceId]);

  useEffect(() => {
    setCfgLoading(true);
    vitrineGetSettings({ request, accountId, workspaceId })
      .then(s => {
        // «Список артикулов» работал только через parser.market; с локальным агентом
        // такой сценарий не сохраняется (400) — переводим на «Весь ассортимент».
        setScenarioType(s.scenario_type === 'custom' ? 'custom' : 'all');
        setCustomSource(s.custom_source || 'url');
        setCustomValue(s.custom_value || '');
        setExecutor('windows_agent');
        setLegacyParserMarket(s.executor === 'parser_market' && !!s.is_active);
        setSelected(new Set(s.offer_ids || []));
        setRegion(s.region || 'Москва');
        setMode(s.mode === 'auto' ? 'auto' : 'manual');
        setSchedule(s.schedule_json && s.schedule_json.length === 168 ? s.schedule_json : emptySchedule());
        setIsActive(!!s.is_active);
      })
      .catch(() => showStatus('error', 'Ошибка загрузки настроек'))
      .finally(() => setCfgLoading(false));
  }, [accountId, workspaceId]); // eslint-disable-line

  useEffect(() => { loadGrouped(); }, [loadGrouped]);

  useEffect(() => {
    loadPivot();

    (async () => {
      const rows = [];
      let offset = 0;
      for (;;) {
        const data = await request(`/api/v1/ozon/${accountId}/assortment?limit=500&offset=${offset}`);
        const page = data?.rows || [];
        rows.push(...page);
        offset += page.length;
        if (!page.length || offset >= Number(data?.total || 0)) break;
      }
      return rows;
    })()
      .then(rows => setAllProducts(rows))
      .catch(() => showStatus('error', 'Ошибка загрузки ассортимента'));

    loadTasks()
      .catch(() => {});
  }, [accountId, workspaceId]); // eslint-disable-line

  useEffect(() => {
    if (!activeTask) return;
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const task = await vitrinePollTask({ request, accountId, taskId: activeTask.id });
        setActiveTask(task);
        if (task.status === 'done' || task.status === 'partial' || task.status === 'error') {
          clearInterval(pollRef.current);
          setActiveTask(null);
          setLoadingCheck(false);
          if (task.status === 'done') {
            showStatus('success', `Готово: ${task.completed_skus} цен получено`);
            loadPivot();
            loadGrouped();
          } else if (task.status === 'partial') {
            showStatus('warning', `Частично: ${task.completed_skus}/${task.total_skus} цен. ${task.error_msg || 'Проверьте полноту результата'}`);
            loadPivot();
            loadGrouped();
          } else {
            showStatus('error', 'Ошибка: ' + (task.error_msg || ''));
          }
          loadTasks().catch(() => {});
        }
      } catch (e) {
        clearInterval(pollRef.current);
        setActiveTask(null);
        setLoadingCheck(false);
        showStatus('error', 'Не удалось обновить состояние задачи: ' + e.message);
      }
    }, 5000);
    return () => clearInterval(pollRef.current);
  }, [activeTask?.id]); // eslint-disable-line

  const handleRun = async () => {
    setLoadingCheck(true);
    try {
      const task = await vitrineRequestCheck({ request, accountId, workspaceId });
      setActiveTask(task);
      showStatus(
        'info',
        `Запрос отправлен: ${task.total_skus} SKU · ${Number(task.billed_amount).toFixed(2)} ₽ · ${task.region}`,
      );
    } catch (e) {
      setLoadingCheck(false);
      throw e;
    }
  };

  const retryLocalTask = async (task) => {
    setRetryingTaskId(task.id);
    try {
      const run = await vitrineRequeueTask({ request, accountId, taskId: task.id });
      const active = {
        id: run.id,
        status: 'pending',
        run_status: run.status,
        executor: 'windows_agent',
        region: 'Регион сессии Ozon',
        scenario_name: 'Весь магазин по ссылке',
        completed_skus: 0,
        total_skus: 0,
        billed_amount: 0,
      };
      setActiveTask(active);
      setLoadingCheck(true);
      showStatus('success', 'Задание возвращено в общую очередь');
      loadTasks().catch(() => {});
    } catch (e) {
      showStatus('error', 'Не удалось повторить задачу: ' + e.message);
    } finally {
      setRetryingTaskId(null);
    }
  };

  // Очередь локального агента общая, и чужоё брошенное задание раньше висело сутками
  // без способа снять его из интерфейса.
  const cancelLocalTask = async (task) => {
    if (!window.confirm('Отменить это задание и убрать его из очереди?')) return;
    setCancellingTaskId(task.id);
    try {
      await vitrineCancelTask({ request, accountId, taskId: task.id });
      if (activeTask && activeTask.id === task.id) {
        setActiveTask(null);
        setLoadingCheck(false);
      }
      showStatus('success', 'Задание снято с очереди');
      loadTasks().catch(() => {});
    } catch (e) {
      showStatus('error', 'Не удалось отменить задачу: ' + e.message);
    } finally {
      setCancellingTaskId(null);
    }
  };

  const taskStats = useMemo(() => ({
    done: recentTasks.filter(t => t.status === 'done').length,
    partial: recentTasks.filter(t => t.status === 'partial').length,
    errors: recentTasks.filter(t => t.status === 'error').length,
    pending: recentTasks.filter(t => t.status === 'pending' || t.status === 'processing').length,
  }), [recentTasks]);

  return (
    <div className="vm">
      <div className="vm__header">
        <div className="vm__header-left">
          <h3 className="vm__title">Мониторинг витринных цен</h3>
          <p className="vm__desc">Цена на витрине Ozon с учётом соинвеста</p>
        </div>
      </div>

      <MonitorConfigPanel
        accountId={accountId}
        workspaceId={workspaceId}
        loading={cfgLoading}
        executor={executor} setExecutor={setExecutor}
        legacyParserMarket={legacyParserMarket}
        scenarioType={scenarioType} setScenarioType={setScenarioType}
        customSource={customSource} setCustomSource={setCustomSource}
        customValue={customValue} setCustomValue={setCustomValue}
        region={region}
        mode={mode} setMode={setMode}
        schedule={schedule} setSchedule={setSchedule}
        isActive={isActive} setIsActive={setIsActive}
        selectedCount={selected.size}
        offerIds={[...selected]}
        onPasteApply={handlePasteApply}
        onClearAll={() => setSelected(new Set())}
        loadingCheck={loadingCheck}
        onRun={handleRun}
      />

      {activeTask && (
        <div className="vm__progress">
          <div className="vm__progress-spinner" />
          <span className="vm__progress-text">
            {activeTask.executor === 'windows_agent'
              ? activeTask.run_status === 'waiting_device' || activeTask.run_status === 'queued'
                ? 'Задача ждёт включения локального агента'
                : `Локальный агент выполняет обход магазина (${activeTask.region})`
              : `Получаем цены (${activeTask.region}${activeTask.scenario_name ? ` · ${activeTask.scenario_name}` : ''})… ${activeTask.completed_skus}/${activeTask.total_skus} SKU`}
          </span>
          <span className="vm__progress-cost">
            {activeTask.executor === 'windows_agent' ? 'Бесплатно' : `−${Number(activeTask.billed_amount).toFixed(2)} ₽`}
          </span>
          {activeTask.executor === 'windows_agent' && (
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => cancelLocalTask(activeTask)}
              disabled={cancellingTaskId === activeTask.id}
            >
              {cancellingTaskId === activeTask.id ? 'Отменяем...' : 'Отменить'}
            </button>
          )}
        </div>
      )}

      <div className="vm__section">
        <div className="vm__section-header">
          <div>
            <strong>Последние задания</strong>
            <div className="vm-cfg__hint">
              Успешно и сохранено на сервере: {taskStats.done} · Частично: {taskStats.partial} · Выполняется: {taskStats.pending} · С ошибками: {taskStats.errors}
            </div>
          </div>
          <button className="btn btn--ghost btn--sm" onClick={() => loadTasks().catch(e => showStatus('error', e.message))}>
            Обновить
          </button>
        </div>
        {recentTasks.length > 0 && taskStats.errors === 0 && taskStats.partial === 0 && taskStats.pending === 0 && (
          <div className="vm-cfg__hint">Все последние задания выполнены без ошибок — кайф. Молодцы.</div>
        )}
        {recentTasks.slice(0, 10).map(task => (
          <div key={task.id} className="vm-cfg__save-row">
            <span>
              {fmtDT(task.created_at)} · {task.executor === 'windows_agent' ? 'Локальный агент' : 'Сервис'} ·{' '}
              {task.status === 'done' ? `сохранено ${task.completed_skus} цен` :
                task.status === 'partial' ? `частично: ${task.completed_skus}/${task.total_skus} цен · ${task.error_msg || 'проверьте результат'}` :
                task.status === 'error' ? `ошибка: ${task.error_msg || 'без описания'}` : 'выполняется'}
            </span>
            {task.status !== 'done' && task.status !== 'partial' && task.executor === 'windows_agent' && (
              <span className="vm-cfg__task-actions">
                <button
                  className="btn btn--secondary btn--sm"
                  onClick={() => retryLocalTask(task)}
                  disabled={retryingTaskId === task.id || cancellingTaskId === task.id}
                >
                  {retryingTaskId === task.id ? 'Возвращаем...' : 'Вернуть в очередь'}
                </button>
                {task.status !== 'error' && (
                  <button
                    className="btn btn--ghost btn--sm"
                    onClick={() => cancelLocalTask(task)}
                    disabled={cancellingTaskId === task.id || retryingTaskId === task.id}
                    title="Снять задание с общей очереди локального агента"
                  >
                    {cancellingTaskId === task.id ? 'Отменяем...' : 'Отменить'}
                  </button>
                )}
              </span>
            )}
          </div>
        ))}
        {!recentTasks.length && <div className="vm-cfg__hint">Запусков пока нет.</div>}
      </div>

      <div className="vm__section">
        <div className="vm__section-header">
          <div className="vm__tabs">
            <button
              className={`vm__tab${tab === 'prices' ? ' vm__tab--active' : ''}`}
              onClick={() => setTab('prices')}
            >
              Витринные цены
            </button>
            <button
              className={`vm__tab${tab === 'coinvest' ? ' vm__tab--active' : ''}`}
              onClick={() => setTab('coinvest')}
            >
              Соинвест — динамика
            </button>
          </div>
          {tab === 'prices' && (
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => { loadPivot(); loadGrouped(); }}
              disabled={loadingPivot || loadingGrouped}
            >
              {loadingPivot || loadingGrouped ? '...' : 'Обновить'}
            </button>
          )}
        </div>

        {tab === 'prices' && (
          <>
            <CoinvestDashboard pivot={pivot} weekly={weekly} />
            <PivotTable
              grouped={grouped}
              loading={loadingGrouped}
              allProducts={allProducts}
              selectable={scenarioType === 'manual'}
              selected={selected}
              onToggle={toggleOffer}
              onToggleMany={toggleOffers}
              onExpand={expandNode}
              onCollapse={collapseNode}
              months={months}
              onMonthsChange={setMonths}
            />
          </>
        )}

        {tab === 'coinvest' && (
          <CoinvestDynamicsTable accountId={accountId} />
        )}
      </div>
    </div>
  );
}
